import { useState } from 'react';
import { useTrackActivity } from '@/hooks/useActivityTracker';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Bell, Shield, Save, CreditCard, Loader2, ExternalLink, Crown, Globe } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getTierName, getTierByProductId, SUBSCRIPTION_TIERS } from '@/lib/subscriptions';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { SecurityReportGenerator } from '@/components/SecurityReportGenerator';
import { EU_LANGUAGES, LanguageCode } from '@/lib/languages';

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { 
    isSubscribed, 
    productId, 
    subscriptionEnd, 
    maxMonitorings, 
    exportEnabled,
    isAdmin,
    refreshSubscription 
  } = useSubscription();
  
  const trackActivity = useTrackActivity();
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  const tierKey = productId ? getTierByProductId(productId) : null;
  const tierName = productId ? getTierName(productId) : null;
  const tier = tierKey ? SUBSCRIPTION_TIERS[tierKey] : null;

  const updateProfile = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', user.id);

      if (error) throw error;
      toast({ title: t('settings.profileUpdated', { defaultValue: 'Profilo aggiornato' }) });
      trackActivity('settings_update', { field: 'full_name' });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error', { defaultValue: 'Errore' }),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast({
        variant: "destructive",
        title: t('settings.passwordMismatch', { defaultValue: 'Le password non corrispondono' }),
      });
      return;
    }

    if (passwordData.new.length < 6) {
      toast({
        variant: "destructive",
        title: t('settings.passwordTooShort', { defaultValue: 'Password troppo corta' }),
        description: t('settings.passwordMinLength', { defaultValue: 'La password deve essere di almeno 6 caratteri' }),
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.new,
      });

      if (error) throw error;

      toast({ title: t('settings.passwordUpdated', { defaultValue: 'Password aggiornata' }) });
      trackActivity('settings_update', { field: 'password' });
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error', { defaultValue: 'Errore' }),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Portal error:', error);
      toast({
        variant: "destructive",
        title: t('common.error', { defaultValue: 'Errore' }),
        description: error.message || t('settings.portalError', { defaultValue: 'Errore durante l\'apertura del portale' }),
      });
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('nav.settings', { defaultValue: 'Impostazioni' })}</h1>
          <p className="text-muted-foreground mt-1">
            {t('settings.subtitle', { defaultValue: 'Gestisci il tuo profilo e le preferenze' })}
          </p>
        </div>

        {/* Subscription Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('settings.subscription', { defaultValue: 'Abbonamento' })}
            </CardTitle>
            <CardDescription>
              {t('settings.subscriptionDescription', { defaultValue: 'Gestisci il tuo piano e la fatturazione' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdmin ? (
              <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <Crown className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium text-foreground">
                    {t('settings.adminAccess', { defaultValue: 'Accesso Amministratore' })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.adminAccessDescription', { defaultValue: 'Hai accesso illimitato a tutte le funzionalità' })}
                  </p>
                </div>
              </div>
            ) : isSubscribed && tierName ? (
              <>
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{tierName}</p>
                      <Badge variant="secondary" className="text-xs">
                        {t('pricing.currentPlan', { defaultValue: 'Piano Attuale' })}
                      </Badge>
                    </div>
                    {subscriptionEnd && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('settings.autoRenewal')}{' '}
                        {format(new Date(subscriptionEnd), 'dd MMMM yyyy', { locale: it })}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground">€{tier?.price}</p>
                    <p className="text-xs text-muted-foreground">/{t('pricing.monthly', { defaultValue: 'mese' })}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">{t('settings.users', { defaultValue: 'Utenti' })}</p>
                    <p className="font-medium text-foreground">{tier?.users}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">{t('settings.monitorings', { defaultValue: 'Monitoraggi' })}</p>
                    <p className="font-medium text-foreground">
                      {maxMonitorings === null ? t('common.unlimited', { defaultValue: 'Illimitati' }) : maxMonitorings}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">{t('settings.export', { defaultValue: 'Esportazione' })}</p>
                    <p className="font-medium text-foreground">
                      {exportEnabled ? t('common.yes', { defaultValue: 'Sì' }) : t('common.no', { defaultValue: 'No' })}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">{t('settings.scope', { defaultValue: 'Copertura' })}</p>
                    <p className="font-medium text-foreground">
                      {tier?.scope === 'european' 
                        ? t('settings.european', { defaultValue: 'Europa' }) 
                        : t('settings.national', { defaultValue: 'Nazionale' })}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={openCustomerPortal} 
                    disabled={portalLoading}
                    className="flex-1"
                  >
                    {portalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    {t('pricing.managePlan', { defaultValue: 'Gestisci Abbonamento' })}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/pricing')}
                  >
                    {t('settings.changePlan', { defaultValue: 'Cambia Piano' })}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-4">
                  {t('settings.noSubscription', { defaultValue: 'Non hai un abbonamento attivo' })}
                </p>
                <Button onClick={() => navigate('/pricing')}>
                  {t('settings.viewPlans', { defaultValue: 'Vedi Piani e Tariffe' })}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('settings.profile', { defaultValue: 'Profilo' })}
            </CardTitle>
            <CardDescription>
              {t('settings.profileDescription', { defaultValue: 'Modifica le informazioni del tuo account' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.emailNotEditable', { defaultValue: "L'email non può essere modificata" })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('settings.fullName', { defaultValue: 'Nome Completo' })}</Label>
              <Input
                id="fullName"
                placeholder={t('settings.yourName', { defaultValue: 'Il tuo nome' })}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <Button onClick={updateProfile} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {t('settings.saveChanges', { defaultValue: 'Salva Modifiche' })}
            </Button>
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t('settings.language')}
            </CardTitle>
            <CardDescription>
              {t('settings.languageDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('settings.preferredLanguage')}</Label>
              <Select
                value={i18n.language}
                onValueChange={async (lang: string) => {
                  i18n.changeLanguage(lang);
                  localStorage.setItem('languageManuallySelected', 'true');
                  document.documentElement.lang = lang;
                  // Save to user profile in DB
                  if (user) {
                    await supabase
                      .from('profiles')
                      .update({ preferred_language: lang } as any)
                      .eq('id', user.id);
                  }
                  toast({ title: t('settings.languageUpdated') });
                  trackActivity('settings_update', { field: 'language', value: lang });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(EU_LANGUAGES) as LanguageCode[]).map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {EU_LANGUAGES[lang].name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('settings.languageNote')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {t('settings.security', { defaultValue: 'Sicurezza' })}
            </CardTitle>
            <CardDescription>
              {t('settings.securityDescription', { defaultValue: 'Gestisci la password del tuo account' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('settings.newPassword', { defaultValue: 'Nuova Password' })}</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder={t('settings.minChars', { defaultValue: 'Minimo 6 caratteri' })}
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('settings.confirmPassword', { defaultValue: 'Conferma Password' })}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t('settings.repeatPassword', { defaultValue: 'Ripeti la nuova password' })}
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
              />
            </div>
            <Button 
              onClick={changePassword} 
              disabled={loading || !passwordData.new}
              variant="outline"
            >
              {t('settings.changePassword', { defaultValue: 'Cambia Password' })}
            </Button>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              {t('settings.notifications', { defaultValue: 'Notifiche' })}
            </CardTitle>
            <CardDescription>
              {t('settings.notificationsDescription', { defaultValue: 'Le preferenze di notifica sono gestite nella sezione Alert' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('settings.alertsLink', { defaultValue: 'Per configurare gli alert email vai alla sezione' })}{' '}
              <a href="/monitoring" className="text-primary hover:underline">
                {t('nav.monitoring', { defaultValue: 'Monitoraggio' })}
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Security Report - Admin Only */}
        {isAdmin && (
          <SecurityReportGenerator />
        )}
      </div>
    </DashboardLayout>
  );
}
